import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FlowjoCommons, CloudActions, CloudErrors } from '@portal/portal-core';

import { FjInput, FjButton } from 'components/primitives';
import { alertsSelector } from 'selectors';
import { composeValidators, navigate, APPLICATION_NAME } from 'utils';
import { validateMinLength, validateMaxLength } from 'utils/validators';

import AuthContainer from '../auth-container';

const { Alerts } = FlowjoCommons;

const linkStyle = {
  color: '#156777',
  background: 'transparent',
  borderColor: 'transparent',
};

interface ResetForm {
  password: string;
  confirmPassword: string;
}

// describe the concrete shape we consume from composeValidators so the FjInput props type-check.
interface ComposedValidator {
  validator: (val: string) => boolean;
  getValidationMessage: (val: string) => string;
}

// composeValidators' `Validator` param typed its `validator` as `(val: unknown) => boolean`, whereas
// the configured length validators (and our inline matcher) narrow to `(val: string)`, which is
// structurally incompatible. Assert the args to the accepted param type at the call sites.
type ValidatorArg = Parameters<typeof composeValidators>[number];

const ResetConfirmation = (): React.JSX.Element => {
  // alertsSelector is an untyped path selector returning `unknown`; alerts is a map of
  // error-type key -> truthy flag.
  const alerts = useSelector(alertsSelector) as Record<string, unknown> | undefined;
  const dispatch = useDispatch();

  const [form, setForm] = useState<ResetForm>({
    password: '',
    confirmPassword: '',
  });

  const onValueChange = (key: keyof ResetForm, value: string): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    const resetCode = Alerts.AlertUtils.getQueryVariable('passwordResetCode');
    const userId = Alerts.AlertUtils.getQueryVariable('userId');
    dispatch(CloudActions.resetForgottenPassword(userId, resetCode, form.password));
  };

  const renderPassword = (): React.JSX.Element => {
    const validators = composeValidators(
      validateMinLength(8) as ValidatorArg,
      validateMaxLength(256) as ValidatorArg,
    ) as ComposedValidator;
    return (
      <FjInput
        type="password"
        id="password"
        autoComplete="new-password"
        value={form.password}
        onChange={(val: string) => onValueChange('password', val)}
        placeholder="Password"
        borderColor="#156777"
        className="full-width margin-bottom-1"
        height={35}
        validator={validators.validator}
        getValidationMessage={validators.getValidationMessage}
      />
    );
  };

  const renderConfirmPassword = (): React.JSX.Element => {
    const validators = composeValidators(
      validateMinLength(8) as ValidatorArg,
      validateMaxLength(256) as ValidatorArg,
      {
        validator: (val: string) => val === form.password,
        getValidationMessage: () => 'Password inputs must match.',
      } as ValidatorArg,
    ) as ComposedValidator;
    return (
      <FjInput
        type="password"
        id="confim-password"
        autoComplete="new-password"
        value={form.confirmPassword}
        onChange={(val: string) => onValueChange('confirmPassword', val)}
        placeholder="Confirm Password"
        borderColor="#156777"
        className="full-width margin-bottom-1"
        height={35}
        validator={validators.validator}
        getValidationMessage={validators.getValidationMessage}
      />
    );
  };

  const renderSubmitButton = (): React.JSX.Element => {
    const { password, confirmPassword } = form;
    const invalid = password.length < 8 || password.length > 256 || password !== confirmPassword;
    return (
      <FjButton
        defaultStyle={{
          background: '#fff',
          color: '#156777',
          borderColor: '#156777',
        }}
        hoverStyle={{
          background: '#156777',
          color: '#fff',
          borderColor: '#156777',
        }}
        type="submit"
        disabled={invalid}
        className="full-width font-size-1-em margin-bottom-sm"
        id="submit-button"
      >
        Submit
      </FjButton>
    );
  };

  const renderSignInLink = (): React.JSX.Element => (
    <FjButton
      className="return-to-profile change"
      defaultStyle={linkStyle}
      hoverStyle={linkStyle}
      type="button"
      onClick={() => navigate('/')}
      height={20}
      width={50}
      id="sign-in-link"
    >
      Return to Sign In
    </FjButton>
  );

  const renderReturnToSignInButton = (): React.JSX.Element => (
    <FjButton
      className="return-to-sign-in forgot"
      defaultStyle={linkStyle}
      hoverStyle={linkStyle}
      type="button"
      onClick={() => navigate('/')}
      height={20}
      width={50}
      id="sign-in-button"
    >
      Return to Sign In
    </FjButton>
  );

  const renderReturnToForgotPassword = (): React.JSX.Element => (
    <FjButton
      className="return-to-sign-in forgot"
      defaultStyle={linkStyle}
      hoverStyle={linkStyle}
      type="button"
      onClick={() => navigate('/forgot-password')}
      height={20}
      width={50}
      id="forgot-password-button"
    >
      Forgot Password?
    </FjButton>
  );

  if (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.SUCCESS_PASSWORD_RESET]) {
    return (
      <AuthContainer title={`${APPLICATION_NAME} Password Reset`}>
        <div className="fj-grey-font margin-bottom-1">
          <span data-test="success-alert">Your password was reset successfully.</span>
        </div>
        {renderReturnToSignInButton()}
      </AuthContainer>
    );
  }
  if (
    (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.INVALID_PASSWORD_RESET_CODE]) ||
    (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.EXPIRED_PASSWORD_RESET_CODE])
  ) {
    return (
      <AuthContainer title={`${APPLICATION_NAME} Password Reset`}>
        <div className="fj-error-container margin-bottom-1">
          <span data-test="invalid-alert" className="fj-prevalidation">
            The password reset link is invalid or expired. Please submit a new request to reset your
            password.
          </span>
        </div>
        {renderReturnToForgotPassword()}
      </AuthContainer>
    );
  }

  return (
    <AuthContainer title={`${APPLICATION_NAME} Password Reset`}>
      <h5 className="fj-grey-font margin-bottom-1">Please enter your new password.</h5>
      <form
        className="display-flex flex-column flex-align-center full-width"
        onSubmit={handleSubmit}
        data-test="password-reset-form"
      >
        {renderPassword()}
        {renderConfirmPassword()}
        {renderSubmitButton()}
        {renderSignInLink()}
      </form>
    </AuthContainer>
  );
};

export default ResetConfirmation;
